import {
  Button,
  DocumentList,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  InfoPanel,
  TextField,
  Typography,
} from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useBusinessRule } from "../../../hooks/businessRule/useBusinessRule";
import OtherParent from "../../../models/Person/OtherParent";
import OtherParentDataCheckStatusEnum from "../../../models/Person/OtherParentDataCheckStatusEnum";
import ParentshipTypeEnum from "../../../models/Person/ParentshipTypeEnum";
import { useOtherParentDocuments } from "../../Request/OtherParentDataStep/useOtherParentDocuments";
import styles from "./RequestDetailsPage.module.scss";

interface Props {
  emptyValue: string;
  otherParent: OtherParent;
  moveToSpecificStep?: (step: number, substep?: number) => void;
  onExpandablePanelClick?: () => void;
}

export default function OtherParentDetails({
  emptyValue,
  otherParent,
  moveToSpecificStep,
  onExpandablePanelClick,
}: Props) {
  const { t } = useTranslation();
  const { isApplicantParent } = useBusinessRule();

  const { handleDownloadDocument } = useOtherParentDocuments();

  return (
    <>
      <Row className={styles.rowSpace}>
        <Col md={12}>
          <ExpandablePanel clickableHeader>
            <ExpandablePanelHeader onClick={onExpandablePanelClick}>
              {t("requestReviewStep.otherParent")}
            </ExpandablePanelHeader>
            <ExpandablePanelBody>
              <Row className={styles.rowHeadingSpace}>
                <Col md={10}>
                  <Typography variant="smallHeading">
                    {t("person.personalInformation")}
                  </Typography>
                </Col>
                {moveToSpecificStep && (
                  <Col md={2}>
                    <Button
                      size="sm"
                      outline
                      onClick={() => moveToSpecificStep(0, 1)}
                    >
                      {t("common.edit")}
                    </Button>
                  </Col>
                )}
              </Row>
              {isApplicantParent(otherParent?.oib) ? (
                <>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.firstName")}
                        helperText={t("common.birthRegistry")}
                        value={otherParent?.firstName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.lastName")}
                        helperText={t("common.birthRegistry")}
                        value={otherParent?.lastName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.oib")}
                        helperText={t("common.oibApi")}
                        value={otherParent?.oib || emptyValue}
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.dateOfBirth")}
                        helperText={t("common.birthRegistry")}
                        value={
                          otherParent && otherParent.dateOfBirth
                            ? hrDate(otherParent?.dateOfBirth)
                            : emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.sex")}
                        helperText={t("common.birthRegistry")}
                        value={otherParent?.sexName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.birthplace")}
                        helperText={t("common.birthRegistry")}
                        value={otherParent?.birthplaceName || emptyValue}
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.countryBirth")}
                        helperText={t("common.birthRegistry")}
                        value={otherParent?.countryOfBirthName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.citizenship")}
                        helperText={t("common.birthRegistry")}
                        value={otherParent?.citizenshipName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.nationalMinority")}
                        value={otherParent.nationalMinorityName || emptyValue}
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.nationality")}
                        value={otherParent.nationalityName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.profession")}
                        value={otherParent.professionName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.professionalQualification")}
                        value={
                          otherParent.professionalQualificationName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="email"
                        label={t("person.email")}
                        value={otherParent?.email || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.phoneNumber")}
                        value={otherParent?.phoneNumber || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.parentshipType")}
                        value={otherParent.parentshipTypeName || emptyValue}
                        plaintext
                      />
                    </Col>
                  </Row>
                  {otherParent.parentshipTypeId ===
                    ParentshipTypeEnum.Custodian && (
                    <Row className={styles.rowSpace}>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.custodian")}
                          value={otherParent.custody || emptyValue}
                          plaintext
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className={styles.rowHeadingSpace}>
                    <Col md={12}>
                      <Typography variant="smallHeading">
                        {t("person.permanentResidenceInformation")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row className={styles.rowHeadingSpace}>
                    <Col md={12}>
                      {otherParent.integrationResultStatus !== undefined &&
                      otherParent.integrationResultStatus ===
                        OtherParentDataCheckStatusEnum.OibFailed ? (
                        <InfoPanel
                          message={t("person.parentPermanentResidenceError")}
                          color="danger"
                          className={styles.errorMessage}
                        />
                      ) : (
                        <InfoPanel
                          message={t("common.mupResidenceInfoMessage")}
                          color="success"
                        />
                      )}
                    </Col>
                  </Row>

                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.country"
                        )}`}
                        value={
                          otherParent?.permanentResidence?.countryName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.municipality"
                        )}`}
                        value={
                          otherParent?.permanentResidence?.municipalityName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.town"
                        )}`}
                        value={
                          otherParent?.permanentResidence?.townName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.postalCode"
                        )}`}
                        value={
                          otherParent?.permanentResidence?.townPostalCode ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.street"
                        )}`}
                        value={
                          otherParent?.permanentResidence?.street || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.streetNumber"
                        )}`}
                        value={
                          otherParent?.permanentResidence?.streetNumber ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={`${t("person.permanentResidence")} - ${t(
                          "person.streetNumberAddition"
                        )}`}
                        value={
                          otherParent?.permanentResidence
                            ?.streetNumberAddition || emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  {otherParent?.temporaryResidence && (
                    <>
                      <Row className={styles.rowHeadingSpace}>
                        <Col md={12}>
                          <Typography variant="smallHeading">
                            {t("person.temporaryResidenceInformation")}
                          </Typography>
                        </Col>
                      </Row>
                      <Row className={styles.rowSpace}>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.country"
                            )}`}
                            value={
                              otherParent?.temporaryResidence?.countryName ||
                              emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.municipality"
                            )}`}
                            value={
                              otherParent?.temporaryResidence
                                ?.municipalityName || emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.town"
                            )}`}
                            value={
                              otherParent?.temporaryResidence?.townName ||
                              emptyValue
                            }
                            plaintext
                          />
                        </Col>
                      </Row>
                      <Row className={styles.rowSpace}>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.postalCode"
                            )}`}
                            value={
                              otherParent?.temporaryResidence?.townPostalCode ||
                              emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.street"
                            )}`}
                            value={
                              otherParent?.temporaryResidence?.street ||
                              emptyValue
                            }
                            plaintext
                          />
                        </Col>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.streetNumber"
                            )}`}
                            value={
                              otherParent?.temporaryResidence?.streetNumber ||
                              emptyValue
                            }
                            plaintext
                          />
                        </Col>
                      </Row>
                      <Row className={styles.rowSpace}>
                        <Col md={4}>
                          <TextField
                            type="text"
                            label={`${t("person.temporaryResidence")} - ${t(
                              "person.streetNumberAddition"
                            )}`}
                            value={
                              otherParent?.temporaryResidence
                                ?.streetNumberAddition || emptyValue
                            }
                            plaintext
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.firstName")}
                        helperText={t("common.birthRegistry")}
                        value={otherParent.firstName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.lastName")}
                        helperText={t("common.birthRegistry")}
                        value={otherParent.lastName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      {otherParent.isOibNotExistent === false && (
                        <TextField
                          type="text"
                          label={t("person.oib")}
                          helperText={t("common.oibApi")}
                          value={otherParent.oib || emptyValue}
                          plaintext
                        />
                      )}
                    </Col>
                  </Row>
                  {otherParent.isOibNotExistent === true && (
                    <Row className={styles.rowSpace}>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.documentNumber")}
                          value={`${otherParent.documentNumber}.` || emptyValue}
                          plaintext
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.documentTypeId")}
                          value={otherParent.documentTypeName || emptyValue}
                          plaintext
                        />
                      </Col>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.reasonNonExistenceOibId")}
                          value={
                            otherParent.reasonNonExistenceOibName || emptyValue
                          }
                          plaintext
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className={styles.rowSpace}>
                    {otherParent.isOibNotExistent === false && (
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.yearOfBirth")}
                          value={`${otherParent.yearOfBirth}.` || emptyValue}
                          plaintext
                        />
                      </Col>
                    )}
                    {otherParent.isOibNotExistent === true && (
                      <>
                        <Col md={2}>
                          <TextField
                            type="text"
                            label={t("person.yearOfBirth")}
                            value={`${otherParent.yearOfBirth}.` || emptyValue}
                            plaintext
                          />
                        </Col>
                        <Col md={2}>
                          <TextField
                            type="text"
                            label={t("person.sex")}
                            value={otherParent.sexName || emptyValue}
                            plaintext
                          />
                        </Col>
                      </>
                    )}
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.profession")}
                        value={otherParent.professionName || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.professionalQualification")}
                        value={
                          otherParent.professionalQualificationName ||
                          emptyValue
                        }
                        plaintext
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpace}>
                    <Col md={4}>
                      <TextField
                        type={otherParent.email ? "email" : "text"}
                        label={t("person.email")}
                        value={otherParent.email || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.phoneNumber")}
                        value={otherParent.phoneNumber || emptyValue}
                        plaintext
                      />
                    </Col>
                    <Col md={4}>
                      <TextField
                        type="text"
                        label={t("person.parentshipType")}
                        value={otherParent.parentshipTypeName || emptyValue}
                        plaintext
                      />
                    </Col>
                  </Row>
                  {otherParent.parentshipTypeId ===
                    ParentshipTypeEnum.Custodian && (
                    <Row className={styles.rowSpace}>
                      <Col md={4}>
                        <TextField
                          type="text"
                          label={t("person.custodian")}
                          value={otherParent.custody || emptyValue}
                          plaintext
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className={styles.rowSpace}>
                    <Col md={12}>
                      <Typography variant="smallHeading">
                        {t("documentUpload.additionalDocumentationList")}
                      </Typography>
                      <DocumentList
                        documents={otherParent?.documents || []}
                        onDocumentClick={handleDownloadDocument}
                        removable={false}
                        fullWidth
                      />
                    </Col>
                  </Row>
                </>
              )}
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>
    </>
  );
}
